import { getWeekKey } from 'common/src/utils/functions';
import { Settlement } from 'common/src/utils/types';
import {
  GroupedSettlement,
  MainClientDetailWithComparable,
} from '../comparative-clientes/comparative-clients.utility';

interface BoxPlotData {
  own:
    | {
        label?: string;
        receiver?: string;
        value: number;
        kilos: number;
      }[]
    | null;
  others: {
    min: number;
    q1: number;
    median: number;
    q3: number;
    max: number;
  };
  othersKg?: number;
}

interface WeekData {
  [caliber: string]: BoxPlotData;
}

interface VarietyData {
  [week: string]: WeekData;
}

export interface IndustryData {
  [variety: string]: VarietyData;
}

const INDUSTRY_KEY = 'INDUSTRIA';
const ALL_CLIENTS = '__all__';

/**
 * Groups settlements for industry comparison:
 * - Maritime: by ship_trip_id (each trip is its own group)
 * - Air: by ETA week
 * - Ground: by ETA week
 */
export function groupSettlementsByShipTrip(
  settlements: Settlement[]
): Record<string, Settlement[]> {
  const groups: Record<string, Settlement[]> = {};

  settlements.forEach((settlement) => {
    let key: string;
    const shipType = settlement.ship_trip?.ship?.type;

    if (shipType === 'A') {
      const weekKey = getWeekKey(settlement.ship_trip?.eta);
      key = `AIR-${weekKey}`;
    } else if (shipType === 'T') {
      const weekKey = getWeekKey(settlement.ship_trip?.eta);
      key = `GROUND-${weekKey}`;
    } else {
      // Maritime: group by ship_trip_id
      const tripId = settlement.ship_trip_id ?? settlement.ship_trip?.id;
      const shipName = (settlement.ship_trip?.ship?.name || 'UNKNOWN')
        .trim()
        .replace(/\s+/g, '_');
      key = `SHIP-${shipName}-${tripId}`;
    }

    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(settlement);
  });

  return groups;
}

/**
 * Extracts unique ship_trip_ids from a list of settlements.
 */
export function getShipTripIds(settlements: Settlement[]): string[] {
  const ids = new Set<string>();
  settlements.forEach((s) => {
    const id = s.ship_trip_id ?? s.ship_trip?.id;
    if (id != null) ids.add(String(id));
  });
  return [...ids];
}

const getRevenueTotal = (detail: any): number => {
  const revenues = detail.settlement_detail_revenues;
  if (!revenues || !Array.isArray(revenues) || revenues.length === 0) return 0;
  return revenues.reduce((sum: number, r: any) => sum + (r.amount_usd || 0), 0);
};

const getDetailValue = (
  detail: any,
  settlement: any,
  priceType: 'FOB' | 'CIF',
  includeRevenues = false
): number => {
  const fob = detail.usd_total_fob;
  // Use _originalTotalKilos when available (set before variety filtering)
  // to ensure freight allocation stays consistent regardless of filters
  const totalSettlementKilos = settlement._originalTotalKilos ||
    settlement.settlement_details.reduce(
      (acc: number, d: any) => acc + d.kilos,
      0
    );
  const freightPerKg = settlement.total_freight_usd
    ? settlement.total_freight_usd / totalSettlementKilos
    : 0;
  let base = priceType === 'CIF' ? fob + detail.kilos * freightPerKg : fob;
  if (includeRevenues) {
    base += getRevenueTotal(detail);
  }
  return base;
};

export interface FiveNumberSummary {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
}

/**
 * Finds the five-number summary and othersKg for a given variety-caliber inside an IndustryData response.
 * The edge function groups by week even when filtered by ship_trip_ids,
 * so we iterate over all weeks to find the matching data.
 */
function findIndustryStats(
  industryData: IndustryData,
  variety: string,
  caliber: string
): { median: number; q1: number; q3: number; min: number; max: number; othersKg: number } | null {
  const varietyData = industryData?.[variety];
  if (!varietyData) return null;

  for (const weekData of Object.values(varietyData)) {
    const entry = weekData?.[caliber];
    const median = entry?.others?.median;
    if (median != null && median > 0) {
      return {
        min: entry?.others?.min ?? 0,
        q1: entry?.others?.q1 ?? 0,
        median,
        q3: entry?.others?.q3 ?? 0,
        max: entry?.others?.max ?? 0,
        othersKg: entry?.othersKg ?? 0,
      };
    }
  }
  return null;
}

/**
 * Estimates the percentile of a value within a five-number summary
 * using linear interpolation between breakpoints.
 * Returns a value between 0 and 100.
 */
export function estimatePercentile(value: number, fiveNum: FiveNumberSummary): number {
  const { min, q1, median, q3, max } = fiveNum;

  if (max === min) return 50;
  if (value <= min) return 0;
  if (value >= max) return 100;

  // Interpolate between the breakpoints
  const breakpoints = [
    { pct: 0, val: min },
    { pct: 25, val: q1 },
    { pct: 50, val: median },
    { pct: 75, val: q3 },
    { pct: 100, val: max },
  ];

  for (let i = 1; i < breakpoints.length; i++) {
    if (value <= breakpoints[i].val) {
      const lower = breakpoints[i - 1];
      const upper = breakpoints[i];
      if (upper.val === lower.val) return lower.pct;
      const ratio = (value - lower.val) / (upper.val - lower.val);
      return lower.pct + ratio * (upper.pct - lower.pct);
    }
  }

  return 100;
}

/**
 * Returns the quartile (1-4) a value falls into within a five-number summary.
 * Q1: <= q1, Q2: <= median, Q3: <= q3, Q4: > q3
 */
export function getQuartile(value: number, fiveNum: FiveNumberSummary): 1 | 2 | 3 | 4 {
  if (value <= fiveNum.q1) return 1;
  if (value <= fiveNum.median) return 2;
  if (value <= fiveNum.q3) return 3;
  return 4;
}

export interface QuartileDistribution {
  q1Kilos: number;
  q2Kilos: number;
  q3Kilos: number;
  q4Kilos: number;
  totalKilos: number;
}

export class ComparativeIndustryUtility {
  /**
   * Builds GroupedSettlement data using per-group industry data.
   * `industryDataByGroup` maps each groupKey to the IndustryData response
   * obtained by calling the edge function with that group's ship_trip_ids.
   */
  static buildGroupedSettlementsWithIndustry(
    shipGroups: Record<string, Settlement[]>,
    clientIds: string[],
    industryDataByGroup: Record<string, IndustryData>,
    priceType: 'FOB' | 'CIF',
    comparisonTarget: 'median' | 'q1' | 'q3' = 'median',
    includeRevenues = false
  ): {
    byShips: Record<string, GroupedSettlement>;
    mainClientDetails: MainClientDetailWithComparable[];
  } {
    const result: Record<string, GroupedSettlement> = {};
    const mainClientDetails: MainClientDetailWithComparable[] = [];

    Object.entries(shipGroups).forEach(([groupKey, settlements]) => {
      const groupIndustryData = industryDataByGroup[groupKey];

      result[groupKey] = {
        mainClient: {
          totalKilos: 0,
          totalFob: 0,
          details: {},
          dates: [],
        },
        comparisons: {},
        meta: {},
      };

      // Capture ship meta
      try {
        if (settlements.length > 0) {
          const withEta = settlements.filter((s) => s?.ship_trip?.eta);
          if (withEta.length > 0) {
            const minEta = withEta
              .map((s) => s.ship_trip!.eta)
              .reduce((min, cur) =>
                new Date(cur) < new Date(min) ? cur : min
              );
            result[groupKey].meta!.eta = minEta;
          }
          const withEtd = settlements.filter((s) => s?.ship_trip?.etd);
          if (withEtd.length > 0) {
            const minEtd = withEtd
              .map((s) => s.ship_trip!.etd)
              .reduce((min, cur) =>
                new Date(cur) < new Date(min) ? cur : min
              );
            result[groupKey].meta!.etd = minEtd;
          }
          result[groupKey].meta!.shipName =
            settlements[0]?.ship_trip?.ship?.name || undefined;
        }
      } catch {}

      // Process main client settlements
      settlements.forEach((settlement: any) => {
        if (!clientIds.includes(ALL_CLIENTS) && (!settlement.receiver || !clientIds.includes(settlement.receiver))) return;

        settlement.settlement_details?.forEach((detail: any) => {
          if (!detail.variety || !detail.caliber) return;

          const detailKey = `${detail.variety}-${detail.caliber}`;
          const totalValue = getDetailValue(detail, settlement, priceType, includeRevenues);

          if (!result[groupKey].mainClient.details[detailKey]) {
            result[groupKey].mainClient.details[detailKey] = {
              kilos: 0,
              fob: 0,
              fobPerKg: 0,
            };
          }
          result[groupKey].mainClient.details[detailKey].kilos += detail.kilos;
          result[groupKey].mainClient.details[detailKey].fob += totalValue;
          result[groupKey].mainClient.totalKilos += detail.kilos;
          result[groupKey].mainClient.totalFob += totalValue;
          if (
            !result[groupKey].mainClient.dates.includes(settlement.date_sold)
          ) {
            result[groupKey].mainClient.dates.push(settlement.date_sold);
          }
        });
      });

      // Calculate FOB per kg for main client details
      Object.values(result[groupKey].mainClient.details).forEach((detail) => {
        detail.fobPerKg = detail.kilos > 0 ? detail.fob / detail.kilos : 0;
      });

      // Build synthetic INDUSTRIA comparison from per-group industry median
      if (groupIndustryData) {
        const industryComparison: GroupedSettlement['comparisons'][string] = {
          totalKilos: 0,
          totalFob: 0,
          details: {},
          dates: [],
        };

        Object.entries(result[groupKey].mainClient.details).forEach(
          ([detailKey, mainDetail]) => {
            const [variety, caliber] = detailKey.split('-');

            const stats = findIndustryStats(groupIndustryData, variety, caliber);

            const targetValue = stats != null ? stats[comparisonTarget] : null;
            if (stats != null && targetValue != null && targetValue > 0) {
              const industryFob = targetValue * stats.othersKg;
              industryComparison.details[detailKey] = {
                kilos: stats.othersKg,
                fob: industryFob,
                fobPerKg: targetValue,
              };
              industryComparison.totalKilos += stats.othersKg;
              industryComparison.totalFob += industryFob;
            }
          }
        );

        if (industryComparison.totalKilos > 0) {
          result[groupKey].comparisons[INDUSTRY_KEY] = industryComparison;
        }
      }

      // Build mainClientDetails for detail-level data
      settlements.forEach((settlement: any) => {
        if (!clientIds.includes(ALL_CLIENTS) && (!settlement.receiver || !clientIds.includes(settlement.receiver))) return;

        settlement.settlement_details?.forEach((detail: any) => {
          if (!detail.variety || !detail.caliber) return;

          const variety = detail.variety;
          const caliber = detail.caliber;
          const detailKey = `${variety}-${caliber}`;
          const totalValue = getDetailValue(detail, settlement, priceType, includeRevenues);
          const fobPerKg = detail.kilos > 0 ? totalValue / detail.kilos : 0;

          const compDetail =
            result[groupKey].comparisons[INDUSTRY_KEY]?.details[detailKey];
          const comparisonFobPerKg = compDetail ? compDetail.fobPerKg : null;

          // Find the five-number summary for Q1/Q3 comparison
          const stats = groupIndustryData
            ? findIndustryStats(groupIndustryData, variety, caliber)
            : null;

          const comparisonQ1FobPerKg = stats ? stats.q1 : null;
          const comparisonQ3FobPerKg = stats ? stats.q3 : null;
          const industryFiveNum: FiveNumberSummary | null = stats
            ? { min: stats.min, q1: stats.q1, median: stats.median, q3: stats.q3, max: stats.max }
            : null;

          mainClientDetails.push({
            reference_number: settlement.reference_number,
            groupKey,
            productKey: detailKey,
            client: settlement.receiver || 'Sin Cliente',
            destinationCountry:
              settlement.ship_trip?.market?.country ||
              settlement.market?.country ||
              'Sin País',
            shipTripId: String(
              settlement.ship_trip?.id || settlement.ship_trip_id || ''
            ),
            variety,
            caliber,
            kilos: detail.kilos,
            fob: totalValue,
            fobPerKg,
            settlement_detail_id: detail.id,
            comparisonFobPerKg,
            hasComparison: compDetail != null,
            comparableKilos: compDetail ? compDetail.kilos : 0,
            comparableFob: compDetail ? compDetail.fob : 0,
            differenceUSD: comparisonFobPerKg
              ? detail.kilos * (fobPerKg - comparisonFobPerKg)
              : 0,
            comparisonQ1FobPerKg,
            comparisonQ3FobPerKg,
            differenceQ1USD: comparisonQ1FobPerKg
              ? detail.kilos * (fobPerKg - comparisonQ1FobPerKg)
              : 0,
            differenceQ3USD: comparisonQ3FobPerKg
              ? detail.kilos * (fobPerKg - comparisonQ3FobPerKg)
              : 0,
            industryFiveNum,
          });
        });
      });
    });

    return { byShips: result, mainClientDetails };
  }
}
